import React, { useEffect, useState, useCallback } from "react";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCollectionOxyzo,
  fetchFilteredCollectionOxyzo,
  downloadOxyzoReport
} from "../../../redux/slice/collectionOxyzoReport";
import { HashLoader } from "react-spinners";
import { Card, Button, Input, Select, Space, Table, Alert, Empty } from "antd";
import { StyledTable, StyledButton } from "../components/styleComp";
import { useAuth } from "../../../context/AuthContext";
import jwt_decode from "jwt-decode";

const { Option } = Select;

const CollectionOxyzo = (props) => {
  const [buttonLoading, setButtonLoading] = useState(false);

  const { authState } = useAuth();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    pages: { collectionOxyzo: { page: 1, pageSize: 7 } },
    filters: { collectionOxyzo: {} },
    searchInputs: { collectionOxyzo: "" },
    selectedColumns: { collectionOxyzo: "" },
    hasSearched: { collectionOxyzo: false },
  });

  const [triggerFilter, setTriggerFilter] = useState(null);

  const jwtBearer = authState?.token;
  const tokenDecoded = jwtBearer ? jwt_decode(jwtBearer) : null;
  const jwtPayload = tokenDecoded
    ? {
        group: props?.userDetails?.group_id,
        user: tokenDecoded?.user_id,
        role: props?.userDetails?.role,
        expiry: tokenDecoded?.exp,
      }
    : {};

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state?.pages?.collectionOxyzo;
      dispatch(
        fetchCollectionOxyzo({
          jwtBearer,
          page,
          filters: state?.filters?.collectionOxyzo,
          pageSize,
        })
      );
    }
  }, [jwtBearer, state?.pages?.collectionOxyzo, state?.filters?.oxyzoCollection, dispatch]);

  const {
    data: collectionOxyzoData = [],
    filteredData: filteredCollectionOxyzoData = [],
    totalCount: totalCollectionOxyzoCount,
    loading: isLoadingCollectionOxyzo,
    filteredLoading: isLoadingFilteredCollectionOxyzo,
    error: errorCollectionOxyzo,
  } = useSelector((state) => state?.oxyzoCollection || {});

  const defineColumns = useCallback((data) => {
    if (!data || data?.length === 0) return [];
    const keys = Object.keys(data[0]);
    return keys.map((key) => ({
      title: <strong style={{ fontSize: "18px" }}>{key.replace(/_/g, " ").toUpperCase()}</strong>,
      dataIndex: key,
      key: key,
      render: (text) => <span style={{ fontSize: "16px" }}>{text}</span>,
    }));
  }, []);

  const handleKeyDown = (event, type) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleFilter(type);
    }
  };

  const handleFilter = (type) => {
    trackFeatureAction("Reports","Collection Report","search");
    const columnName = state?.selectedColumns?.[type]?.toLowerCase();
    const trimmedInput = state?.searchInputs?.[type]?.trim();

    dispatch(
      fetchFilteredCollectionOxyzo({
        jwtBearer,
        filters: { [columnName]: trimmedInput },
      })
    );

    setState((prevState) => ({
      ...prevState,
      hasSearched: {
        ...prevState?.hasSearched,
        [type]: true,
      },
      pages: {
        ...prevState?.pages,
        [type]: { ...prevState?.pages?.[type], page: 1 },
      },
    }));
  };

  const handleReset = (type) => {
    trackFeatureAction("Reports","Collection Report","reset");
    setState((prevState) => ({
      ...prevState,
      searchInputs: {
        ...prevState?.searchInputs,
        [type]: "",
      },
      selectedColumns: {
        ...prevState?.selectedColumns,
        [type]: "",
      },
      hasSearched: {
        ...prevState?.hasSearched,
        [type]: false,
      },
      filters: {
        ...prevState?.filters,
        [type]: {},
      },
    }));
    setTriggerFilter(type);
  };

  const paginationConfig = (type) => {
    const total = state?.hasSearched?.collectionOxyzo
      ? filteredCollectionOxyzoData?.length
      : totalCollectionOxyzoCount;

    return {
      current: state?.pages?.collectionOxyzo?.page,
      pageSize: state?.pages?.collectionOxyzo?.pageSize,
      total: total,
      showTotal: (total, [start, end]) => `${start}-${end} of ${total} items`,
      onChange: (page, pageSize) =>
        setState((prevState) => ({
          ...prevState,
          pages: { ...prevState?.pages, [type]: { page, pageSize } },
        })),
    };
  };

  const renderSearchBar = (columns, type) => (
    <Space style={{ marginBottom: 16, marginLeft: 2 }}>
      <Select
        placeholder="Select Column"
        style={{ width: 200, height: 32, lineHeight: "32px" }}
        value={state?.selectedColumns?.[type] || undefined}
        onChange={(value) =>
          setState((prevState) => ({
            ...prevState,
            selectedColumns: {
              ...prevState?.selectedColumns,
              [type]: value,
            },
          }))
        }
      >
        {columns?.map((col) => (
          <Option key={col?.key} value={col?.dataIndex}>
            {col?.title?.props?.children}
          </Option>
        ))}
      </Select>
      <Input
        placeholder="Value"
        value={state?.searchInputs?.[type]}
        onChange={(e) =>
          setState((prevState) => ({
            ...prevState,
            searchInputs: {
              ...prevState?.searchInputs,
              [type]: e.target.value,
            },
          }))
        }
        onKeyDown={(e) => handleKeyDown(e, type)}
        style={{ width: 150, height: 32, lineHeight: "32px" }}
        disabled={!state?.selectedColumns?.[type]}
      />
      <Button
        type="primary"
        onClick={() => handleFilter(type)}
        style={{ height: 32, lineHeight: "32px" }}
      >
        Search
      </Button>
      {state?.hasSearched?.collectionOxyzo && (
        <Button
          onClick={() => handleReset(type)}
          style={{ width: 70, height: 32, lineHeight: "32px" }}
        >
          Reset
        </Button>
      )}
    </Space>
  );

  const renderButtonByType = () => (
    <StyledButton
      type="primary"
      onClick={() => handleAPIDownload()}
      loading={buttonLoading}
    >
      Download Report
    </StyledButton>
  );

  const handleAPIDownload = () => {
    trackFeatureAction("Reports", "Collection Report", "download");
    setButtonLoading(true);
    dispatch(downloadOxyzoReport({ jwtBearer, startDate: "", endDate: "", type: "collection" }))
      .finally(() => setButtonLoading(false));
  };

  return (
    <Card
      title={<span style={{ fontWeight: 600, fontSize: "24px", fontFamily: "poppins-font, sans-serif" }}>COLLECTION REPORT</span>}
      bordered
      extra={
        <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {renderButtonByType()}
          </div>
        </div>
      }
    >
      {renderSearchBar(defineColumns(collectionOxyzoData || []), "collectionOxyzo")}

      {(isLoadingFilteredCollectionOxyzo || isLoadingCollectionOxyzo) ? (
        <div className="spinner-container">
          <HashLoader color="#f57171" loading={true} />
        </div>
      ) : errorCollectionOxyzo ? (
        <Alert message={errorCollectionOxyzo} type="error" />
      ) : (
        <StyledTable
          columns={defineColumns(collectionOxyzoData)}
          dataSource={
            state?.hasSearched?.collectionOxyzo
              ? filteredCollectionOxyzoData
              : collectionOxyzoData
          }
          pagination={paginationConfig("collectionOxyzo")}
          locale={{ emptyText: <Empty description="No data available" /> }}
        />
      )}
    </Card>
  );
};

export default CollectionOxyzo;